import { useEffect, useState } from "react";
import { techStacks } from "../data/techStacks";

// Utility: Slugify a name for consistent keying
function slugify(name) {
  let str = String(name).toLowerCase();
  if (str.includes(".net")) str = str.replace(".net", "dotnet");
  return str
    .replace(/\+/g, "p")
    .replace(/#/g, "s")
    .replace(/\.[^.]+$/, "")
    .replace(/\s+/g, "-")
    .replace(/[^a-z0-9\-_]/g, "");
}

// Load all icons from the icons folder
const req = require.context("../assets/icons", true, /\.(png|jpe?g|svg)$/);
const icons = Object.fromEntries(
  req.keys().map((k) => {
    const file = k.replace(/^.\//, "");
    const key = slugify(file);
    const url = req(k).default || req(k);
    return [key, url];
  })
);

// Load all project images from the projectImages folder
const projectReq = (() => {
  try {
    return require.context("../assets/projectImages", true, /\.(png|jpe?g|svg)$/);
  } catch {
    return null;
  }
})();

const projectImages = projectReq
  ? Object.fromEntries(
      projectReq.keys().map((k) => {
        const file = k.replace(/^.\//, "");
        const key = slugify(file);
        const url = projectReq(k).default || projectReq(k);
        return [key, url];
      })
    )
  : {};

// Get icon URL by name or category/name
export function getIcon(name) {
  if (!name) return icons.default;

  // Try slugified path-like string
  if (typeof name === "string" && name.includes("/")) {
    const key = slugify(name);
    return (
      icons[key] ||
      icons[slugify(name.replace(/\//g, "-"))] ||
      icons[slugify(name.split("/").pop())] ||
      null
    );
  }

  // Try direct slugified key
  const simpleKey = slugify(name);
  if (icons[simpleKey]) return icons[simpleKey];

  // Try to infer category from techStacks
  if (Array.isArray(techStacks)) {
    for (const group of techStacks) {
      if (group.list && group.list.includes(name)) {
        const candidate = slugify(`${group.category}/${name}`);
        if (icons[candidate]) return icons[candidate];
      }
    }
  }

  // Fallbacks
  return icons[name] || null;
}

// Get project image URL by index
export function getProjectImage(project, index = 0) {
  if (!project.images || project.images.length === 0) return null;

  const entry = project.images[index];
  if (!entry) return null;

  if (typeof entry === "string") {
    const key = slugify(entry);
    if (projectImages[key]) return projectImages[key];
    if (/^(https?:)?\/\//.test(entry) || entry.startsWith("/")) return entry;
    return entry;
  }

  if (typeof entry === "object" && entry !== null && entry.src) {
    return entry.src;
  }

  return null;
}

// Responsive view hook
export function useResponsiveView(breakpoint = 768) {
  const [mobileView, setMobileView] = useState(window.innerWidth < breakpoint);

  useEffect(() => {
    const checkScreen = () => setMobileView(window.innerWidth < breakpoint);
    checkScreen();
    window.addEventListener("resize", checkScreen);
    return () => window.removeEventListener("resize", checkScreen);
  }, [breakpoint]);

  return mobileView;
}

export default icons;